import Author from './Author';
import Sponsor from './Sponsor';
import Time from './Time';

export interface DocumentCardMetaProps {
    tag?: 'div' | 'span';
    author?: Author | null;
    sponsor?: Sponsor | null;
    date?: string | null;
    placeholder?: boolean;
    withoutAuthor?: boolean;
    withoutAuthorImage?: boolean;
    withoutTime?: boolean;
    className?: string | null;
    authorClassName?: string | null;
    sponsorClassName?: string | null;
    sponsorLabelClassName?: string | null;
    timeClassName?: string | null;
}

type DocumentCardMetaContent = Pick<
    DocumentCardMetaProps,
    'author' | 'sponsor' | 'date' | 'placeholder' | 'withoutAuthor' | 'withoutTime'
>;

function getContent({
    author = null,
    sponsor = null,
    date = null,
    placeholder = false,
    withoutAuthor = false,
    withoutTime = false,
}: DocumentCardMetaContent) {
    return {
        hasAuthor: (placeholder || (author !== null && sponsor === null)) && !withoutAuthor,
        hasSponsor: sponsor !== null,
        hasTime: date !== null && !withoutTime,
    };
}

/**
 * Whether the meta of a document card shows anything: an author, a sponsor or a date.
 */
export function hasDocumentCardMeta(props: DocumentCardMetaContent) {
    const { hasAuthor, hasSponsor, hasTime } = getContent(props);
    return hasAuthor || hasSponsor || hasTime;
}

/**
 * Meta of the document cards: the author, or the sponsor of a sponsored document, and the date.
 */
function DocumentCardMeta({
    tag: Tag = 'div',
    author = null,
    sponsor = null,
    date = null,
    placeholder = false,
    withoutAuthor = false,
    withoutAuthorImage = false,
    withoutTime = false,
    className = null,
    authorClassName = null,
    sponsorClassName = null,
    sponsorLabelClassName = undefined,
    timeClassName = null,
}: DocumentCardMetaProps) {
    const { hasAuthor, hasSponsor, hasTime } = getContent({
        author,
        sponsor,
        date,
        placeholder,
        withoutAuthor,
        withoutTime,
    });
    return (
        <Tag className={className}>
            {hasAuthor ? (
                <Author
                    {...author}
                    placeholder={placeholder}
                    className={authorClassName}
                    withoutImage={withoutAuthorImage}
                />
            ) : null}
            {hasSponsor ? (
                <Sponsor
                    {...sponsor}
                    className={sponsorClassName}
                    labelClassName={sponsorLabelClassName}
                />
            ) : null}
            {hasTime ? <Time date={date} className={timeClassName} /> : null}
        </Tag>
    );
}

export default DocumentCardMeta;
